/**
 * GuideVideoButton — the ONE way the app and the extension point at a tutorial video.
 *
 * Tiny on purpose: at most one text line tall (24px), placed next to something that
 * already exists, never a row or a box of its own. Three shapes, one blue ▶:
 *   - pill:  "▶ Watch how · 1:16"        (next to a page / section title)
 *   - link:  "▶ Watch the 1-min video"   (empty states, onboarding steps, info bars)
 *   - icon:  ▶ with a "Watch how · 1:16" tooltip (dense headers, the extension panel)
 *
 * It renders NOTHING when the lesson has no video yet (so pending spots ship dark and
 * light up when the website lists an id) and nothing in the white-label `/clear-*`
 * flow (the videos are Blueticks-branded). Clicking opens a 16:9 modal that plays at
 * once; closing it unmounts the iframe.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/common/components/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { namespace } from '@common-config/constants';
import { ReactReduxContext } from 'react-redux';
import { selectCurrentUser } from '@common-reducers/UserSelectors';
import { AnalyticsService } from '@common-services/analytics-service';
import { isClearFlow } from '@common/utils/clear-flow';
import {
  formatDuration,
  getGuideLesson,
  getGuideVideos,
  lessonPageUrl,
  refreshGuideVideos,
  subscribeGuideVideos,
  type GuideLesson,
} from './guide-video-registry';
import { CONTENT_LANGS, readBrowserLangSignals, resolveGuideVideoLang, type ContentLang } from './guide-video-language';

export type GuideVideoVariant = 'pill' | 'link' | 'icon';

export interface GuideVideoButtonProps {
  /** Academy lesson slug, e.g. "message-planner". */
  lesson: string;
  variant: GuideVideoVariant;
  /** Overrides the visible label (pill / link). */
  label?: string;
  /** Where the button sits, for the guide_video_play event. */
  surface?: string;
  style?: React.CSSProperties;
  /** link variant: "Watch how · 1:16" instead of "Watch the 1-min video". */
  withDuration?: boolean;
  /** Extra hook when the video opens (e.g. the onboarding funnel's own event). */
  onPlay?: () => void;
  /** icon variant: glyph size in px (default 18). Use 16 inside a line of body text. */
  size?: number;
}

/** The one blue for every ▶. */
export const GUIDE_VIDEO_BLUE = '#1d9be4';

/** Inside WhatsApp Web (extension content script) the page's CSP/COEP decide what an iframe may load. */
const isInsideWhatsAppWeb = () => {
  try {
    return globalThis.location?.hostname === 'web.whatsapp.com';
  } catch {
    return false;
  }
};

/**
 * Embed URL for the current host.
 * - Web app: youtube-nocookie.
 * - WhatsApp Web: its frame-src allows only *.youtube.com and it sends COEP require-corp,
 *   so the player is youtube.com in a `credentialless` iframe (no cookies — the nocookie
 *   privacy benefit). Tested; see video-plan-app.md §4.
 */
export function guideEmbedSrc(id: string, insideWhatsApp: boolean): string {
  const host = insideWhatsApp ? 'https://www.youtube.com' : 'https://www.youtube-nocookie.com';
  return `${host}/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
}

/** Keeps the bundled registry live: refreshed once per session, re-rendered when it lands. */
export function useGuideLesson(slug: string): GuideLesson | null {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const off = subscribeGuideVideos(() => setVersion((v) => v + 1));
    refreshGuideVideos();
    return off;
  }, []);
  return getGuideLesson(slug, getGuideVideos());
}

export const PlayGlyph: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden focusable="false" style={{ flex: '0 0 auto', display: 'block' }}>
    <circle cx="10" cy="10" r="10" fill={GUIDE_VIDEO_BLUE} />
    <path d="M8 6.2v7.6L14 10z" fill="#fff" />
  </svg>
);

const reset: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  margin: 0,
  font: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

const VARIANT_STYLE: Record<GuideVideoVariant, React.CSSProperties> = {
  pill: { ...reset, height: 24, gap: 6, padding: '0 10px 0 4px', borderRadius: 999, background: 'rgba(29,155,228,.12)', color: GUIDE_VIDEO_BLUE, fontSize: 13, fontWeight: 500 },
  link: { ...reset, height: 20, gap: 6, padding: 0, background: 'transparent', color: GUIDE_VIDEO_BLUE, fontSize: 13, fontWeight: 600 },
  icon: { ...reset, width: 22, height: 22, padding: 0, justifyContent: 'center', borderRadius: 999, background: 'transparent' },
};

export const GuideVideoButton: React.FC<GuideVideoButtonProps> = ({ lesson: slug, variant, label, surface, style, onPlay, withDuration, size }) => {
  const { t } = useTranslation(namespace);
  const lesson = useGuideLesson(slug);
  // Read the user without subscribing (and without requiring a store: a button outside a
  // <Provider> still works, just without the user's language / country signals).
  const redux = useContext(ReactReduxContext);
  const [open, setOpen] = useState(false);

  if (!lesson || isClearFlow()) return null;

  const user = (() => {
    try { return redux?.store ? selectCurrentUser(redux.store.getState()) : undefined; } catch { return undefined; }
  })();
  const browser = readBrowserLangSignals();
  const lang: ContentLang = resolveGuideVideoLang(CONTENT_LANGS.filter((l) => lesson.videos[l]?.id), {
    stored: browser.stored,
    preferredLanguage: user?.preferredLanguage,
    country: user?.countryCode || browser.navigatorRegion,
    languages: browser.languages,
  });

  const duration = formatDuration(lesson.durationSec);
  const watchHow = duration ? t('guideVideo.watchHowDuration', { duration }) : t('guideVideo.watchHow');
  const videoId = lesson.videos[lang]?.id ?? Object.values(lesson.videos).find((v) => v?.id)?.id ?? '';
  const insideWhatsApp = isInsideWhatsAppWeb();
  const fullLessonUrl = lesson.url || lessonPageUrl(slug);

  const onClick = (e: React.MouseEvent) => {
    // Always sits next to something clickable (a card, a row, a CTA): never trigger it.
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    try {
      new AnalyticsService().event('guide_video_play', {
        category: 'guide_video',
        lesson: slug,
        surface: surface ?? (globalThis.location?.pathname || 'unknown'),
        lang,
      });
    } catch { /* analytics never blocks the video */ }
    try { onPlay?.(); } catch { /* the host's hook is best-effort */ }
  };

  const text = label ?? (variant === 'pill' || withDuration ? watchHow : t('guideVideo.watchVideo'));
  const button = (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label={variant === 'icon' ? watchHow : undefined}
      data-testid={`guide-video-${slug}`}
      data-guide-video={slug}
      style={{ ...VARIANT_STYLE[variant], ...(variant === 'icon' && size ? { width: size + 4, height: size + 4 } : null), ...style }}
    >
      <PlayGlyph size={variant === 'icon' ? (size ?? 18) : 16} />
      {variant !== 'icon' && <span>{text}</span>}
    </button>
  );

  // Credentialless is set as a plain attribute (React 18 has no typing for it). The
  // iframe is created detached, so the attribute is in place before it navigates.
  const frameAttrs: Record<string, string> = insideWhatsApp ? { credentialless: '' } : {};

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{watchHow}</TooltipContent>
        </Tooltip>
      ) : button}
      {/* Mounted only while open: no dialog per button at rest, and closing unmounts the iframe. */}
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-[min(880px,calc(100vw-24px))] w-full p-3 gap-2 sm:max-w-[min(880px,calc(100vw-24px))]">
            <DialogTitle className="text-base font-semibold px-1 pe-8">{lesson.title}</DialogTitle>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
              {videoId && (
                <iframe
                  {...frameAttrs}
                  src={guideEmbedSrc(videoId, insideWhatsApp)}
                  aria-label={lesson.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  data-testid="guide-video-iframe"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '2px 4px 0' }}>
              <a
                href={fullLessonUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: GUIDE_VIDEO_BLUE, textDecoration: 'none' }}
              >
                {t('guideVideo.openFullLesson')}
                <ExternalLink size={14} aria-hidden />
              </a>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default GuideVideoButton;
